import * as React from 'react';
import { Button, Input, Select, TextArea } from '@bepure/tarkin';

declare const BASE_API_URL: string;

interface ContactUsState {
    isPosting: boolean;
    posted: boolean;
    error: boolean;
    inputs: {
        [name: string]: string;
    };
}

class ContactUsForm extends React.Component<{}, ContactUsState> {

    private categories = [
        'I have a question about the Eve Hormone Balance Test',
        'I have a question about the Eve Stress & Adrenal Test',
        'I have an issue with my test kit or registration',
        'I have a question about my account information',
        'I have a question about shipping and delivery',
        'I have a question about my hormones',
        'Something else',
    ];

    constructor(props: {}) {
        super(props);
        this.state = {
            inputs: {
                reason: this.categories[0],
            },
            isPosting: false,
            posted: false,
            error: false,
        };
    }

    public componentDidUpdate() {
        if (this.state.error || this.state.posted) {
            window.scrollTo(0, 0);
        }
    }

    public render() {
        if (this.state.error) {
            return (
                <div className="flex flex--column flex--align_start">
                    <p className="text text--heading text--error">Whoops</p>
                    <p className="text text-body text--error">
                        Oh no! It looks like there was an error sending your message. If you’re having trouble, you can give us a call on 0800 367 383 or send an email to <a className="text text--body text--link" href="mailto:info@evewellnessco.com">info@evewellnessco.com</a>.
                    </p>
                    <p className="text text--body text--error">
                        You can copy your message below and try again.
                        </p>
                    <p className="text text--body">
                        {this.state.inputs.message}
                    </p>
                    <Button
                        id="contact_us_form-try_again"
                        text="Try again"
                        ariaLabel="Try contact us again"
                        onClick={this.tryAgain}
                    />
                </div>
            );
        }
        if (this.state.posted) {
            return (
                <div className="flex flex--column flex--align_start">
                    <p className="text text-body">
                    Thanks! Your message has been sent and we’ll be in touch very soon. In the meantime, you can browse our <a className="text text--body text--link" href="/faq">FAQs</a> for more information or head back to our homepage.
                    </p>
                    <p className="text text-body">Got another message you want to send?</p>
                    <Button
                        id="contact_us_form-send_me_a_msg"
                        text="Send new message"
                        ariaLabel="Send a new message"
                        onClick={this.reset}
                    />
                </div>
            );
        }

        const options = this.categories.map(category => ({
            text: category,
            value: category,
            disabled: false,
            hidden: false,
            selected: false,
            sublabel: false,
        }));

        return (
            <form onSubmit={this.onSubmit}>
                <Input
                    label="Name"
                    ariaLabel="Full name"
                    value={this.state.inputs.name}
                    onChange={this.onInputChange('name')}
                    required={true}
                />
                <Input
                    ariaLabel="Email"
                    label="Email"
                    type="email"
                    value={this.state.inputs.email}
                    onChange={this.onInputChange('email')}
                    required={true}
                />
                <Input
                    label="Contact Phone Number"
                    subLabel="What is your best contact number?"
                    type="tel"
                    ariaLabel="Phone number"
                    value={this.state.inputs.phone}
                    onChange={this.onInputChange('phone')}
                    required={true}
                />
                <Select
                    label="Reason For Contacting Us"
                    subLabel="Please select the primary reason for your request"
                    ariaLabel="Contact Reason"
                    options={options}
                    required={true}
                    value={this.state.inputs.reason}
                    onChange={this.onInputChange('reason')}
                    truncate={true}
                />

                <div className="margin--vertical">
                    <TextArea
                        value={this.state.inputs.message}
                        onChange={this.onInputChange('message')}
                        required={true}
                        ariaLabel="Message"
                        label="Message"
                        subLabel="Please enter the details of your request. A member of our support staff will respond as soon as possible."
                    />
                </div>
                <div className="margin--vertical flex flex--justify_center flex--justify_start_desktop">
                    <Button
                        id="contact_us_form-get_in_touch"
                        text="Get in touch"
                        type="submit"
                        ariaLabel="Submit contact us form"
                        disabled={this.state.isPosting}
                    />
                </div>
            </form>
        );
    }
    private onInputChange = (name: string): React.EventHandler<any> => event => {
        this.setState({
            inputs: {
                ...this.state.inputs,
                [name]: event.target.value,
            },
        });
    }

    private onSubmit: React.FormEventHandler = async event => {
        event.preventDefault();
        await this.setState({
            isPosting: true,
        });
        await fetch(`${BASE_API_URL}/v1/user/contact-us/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({data: this.state.inputs}),
        })
            .then(response => {
                if (!response.ok) {
                    throw response;
                }
                return response;
            })
            .then(response => response.json())
            .then(data => {
                this.setState({
                    isPosting: false,
                    posted: true,
                });
            })
            .catch(error => {
                this.setState({
                    isPosting: false,
                    posted: true,
                    error: true,
                });
            });
    }

    private reset: React.MouseEventHandler<HTMLButtonElement> = event => {
        event.currentTarget.blur();
        this.setState({
            inputs: {},
            isPosting: false,
            posted: false,
            error: false,
        });
    }

    private tryAgain: React.MouseEventHandler<HTMLButtonElement> = event => {
        event.currentTarget.blur();
        this.setState({
            isPosting: false,
            posted: false,
            error: false,
        });
    }
}

export default ContactUsForm;
